import template from 'url-template';
import axios from 'axios';
import { get, isEmpty } from 'lodash';

import {
    FETCH_ENTITY_RULES,
    FETCH_ENTITY_SENSORS,
    FETCH_INSPECTION_REPORTS,
    SET_PROCESS_ID,
    FETCH_REPORT_LISTS,
    FETCH_SENSOR_COUNT_REPORT,
    SET_WATCHDOG_REPORTS
} from '../actions/actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { fetchSensorTypes } from './sensorTypeActions';
import { storeQueryParamter } from './dashboardActions';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';
import { setBobLoading } from './bobActions';

const CreateReportUrl = template.parse('/reports/createreport');
const WatchDogReportUrl = template.parse('/site-performance-report');
const DownloadUrl = template.parse('/companies/{companyId}/resource{?resourceId,resourceType}');
const ReportsUrl = template.parse('/worker-process-collection{?processId,processType,groupId,status,userId}');
const ReportUrl = template.parse('/worker-process{?processId,groupId}');
const SitesUrl = template.parse('/clients/{clientId}/sites{?limit,page,searchFilter,sort}');
const EquipmentUrl = template.parse('/sites/{siteId}/equipment{?limit,page,searchFilter,sort,extendedData}');
const SystemsUrl = template.parse('/sites/{siteId}/systems{?limit,page,searchFilter,sort,extendedData}');
const EquipmentTypesUrl = template.parse('/equipment-types{?SiteId}');
const EntityRuleUrl = template.parse('/entities/rule-entity{?queryparameterId,fromDateTime,toDateTime,siteId}');
const SensorsUrl = template.parse('/sensorsdetails{?systemIds,equipmentIds,sensorTypeIds,queryparameterId}');
const SensorCountReportUrl = template.parse('/companies/{companyId}/sensors-count-report');
const CreateOptimizationReportUrl = template.parse('/equipment-optimized-report{?queryParameterId}');
const EntitySensorsUrl = template.parse('/sites/{siteId}/sensors{?limit,page,searchFilter,sort,parentId}');
const BillingMetricsReportUrl = template.parse('/equipment-data{?CompanyId,ClientId}');

const DEFAULT_SORT = 'name^asc';

export const reportingActionRouter = (entity, entityId, action) => async (dispatch, getState) => {
    switch (action) {
        case 'Delete':
            switch (entity) {
                case 'InspectionReports':
                    dispatch(removeInspectionReport(entityId));
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
};

export const createInspectionReport = (body, isMonthly, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();
    body.monthlyreport = isMonthly;

    try {
        const response = await lambdaBaseApi.post(
            CreateReportUrl.expand({
                ...options
            }),
            body,
            header
        );

        const payload = get(response, 'data.data', '');

        dispatch(setProcessId(payload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createWatchDogReport = (body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.post(
            WatchDogReportUrl.expand({
                ...options
            }),
            body,
            header
        );

        const payload = get(response, 'data.processId', '');
        dispatch(setProcessId(payload));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createOptimizationReport = (userId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const parameters = {
            'Process':     23,
            'InputObject': {
                'Filter': {
                    ...body,
                    'IntervalInMinutes': 15
                },
                'ProcessGroupId': '',
                'UserId':         userId,
                'UserTimeZone':   tz
            }
        };

        const queryParameterId = await dispatch(storeQueryParamter(parameters));

        const response = await k2aBaseApi.post(
            CreateOptimizationReportUrl.expand({
                queryParameterId,
                ...options
            }),
            {},
            header
        );
        
        const payload = get(response, 'data.processId', '');

        dispatch(setProcessId(payload));
        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteInspectionReport = (processId, groupId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            ReportUrl.expand({
                processId,
                groupId
            }),
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchInspectionReports = (options = {}) => async dispatch => {
    const header = await awsHeader();

    options.processType || (options.processType = 'InspectionReportCreate');

    try {
        const response = await k2aBaseApi.get(
            ReportsUrl.expand({
                ...options
            }),
            header
        );

        const payload = get(response, 'data', []);
        const results = [];

        payload.forEach(item => {
            results.push({
                clientName: get(item, 'result.clientName', ''),
                reportData: get(item, 'result.reportData', {}),
                processId:  item.processId,
                groupId:    item.processGroupId,
                siteName:   get(item, 'result.siteName', ''),
                status:     item.status,
                targetDate: get(item, 'result.targetDateEnd', ''),
                timestamp:  get(item, 'timestamp', '')
            });
        });

        dispatch(setInspectionReports(results));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchWatchDogReports = (options = {}) => async dispatch => {
    const header = await awsHeader();
    options.processType || (options.processType = 'SitePerformance');

    try {
        const response = await k2aBaseApi.get(
            ReportsUrl.expand({
                ...options
            }),
            header
        );

        const payload = get(response, 'data', []);
        const results = [];

        payload.forEach(item => {
            results.push({
                exportFileKey:   get(item, 'result.exportFileKey', {}),
                clientName:      get(item, 'result.clientName', ''),
                siteName:        get(item, 'result.siteName', ''),
                targetDateEnd:   get(item, 'result.targetDateEnd', ''),
                targetDateStart: get(item, 'result.targetDateStart', ''),
                processId:       item.processId,
                groupId:         item.processGroupId,
                status:          item.status,
                timestamp:       get(item, 'timestamp', '')
            });
        });

        dispatch(setWatchDogReports(results));
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const removeInspectionReport = id => async (dispatch, getState) => {
    const inspectionReports = getState().reporting.inspectionReports;
    const currentApp = getState().core.currentApp;

    const updatedInspectionReports = inspectionReports.filter(report => report.processId !== id);

    dispatch(setInspectionReports(updatedInspectionReports));

    if (currentApp === 'reporting') {
        dispatch(enqueueSnackbar('Report deleted successfully.', 'success'));
    }
};

export const setInspectionReports = payload => async dispatch => {
    dispatch({
        type: FETCH_INSPECTION_REPORTS,
        payload
    });
};

export const setWatchDogReports = payload => async dispatch => {
    dispatch({
        type: SET_WATCHDOG_REPORTS,
        payload
    });
};

export const setProcessId = payload => async dispatch => {
    dispatch({
        type: SET_PROCESS_ID,
        payload
    });
};

export const updateInspectionReport = payload => async (dispatch, getState) => {
    let inspectionReports = getState().reporting.inspectionReports;

    if (inspectionReports) {
        inspectionReports.forEach(report => {
            if (report.processId === payload.processId) {
                report.status = payload.status;
                report.reportData = {...payload.result.reportData};
            }
        });
    }
    else {
        inspectionReports = [];
    }

    dispatch(setInspectionReports(inspectionReports));

    dispatch({
        type: FETCH_REPORT_LISTS,
        payload
    });
};

export const updateWatchDogReport = payload => async (dispatch, getState) => {
    let watchDogReports = getState().reporting.watchDogReports;

    if (watchDogReports) {
        watchDogReports.forEach(report => {
            if (report.processId === payload.processId) {
                report.status = payload.status;
                report.reportData = { ...payload.result.reportData };
            }
        });
    }
    else {
        watchDogReports = [];
    }

    dispatch(setWatchDogReports(watchDogReports));

    dispatch({
        type: FETCH_REPORT_LISTS,
        payload
    });
};

export const fetchReportSites = (clientId, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();

    options.limit || (options.limit = 500);
    options.page || (options.page = 1);

    try {
        const response = await k2aBaseApi.get(
            SitesUrl.expand({
                clientId,
                ...options
            }),
            header
        );

        let sites = get(response, 'data.data', []);

        return sites;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchReportEquipment = (siteId, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();

    options.limit || (options.limit = 1000);
    options.page || (options.page = 1);
    options.extendedData = true;

    try {
        const response = await k2aBaseApi.get(
            EquipmentUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        let equipment = get(response, 'data.data', []);

        return equipment;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchReportSystems = (siteId, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();

    options.limit || (options.limit = 1000);
    options.page || (options.page = 1);
    options.extendedData = true;

    try {
        const response = await k2aBaseApi.get(
            SystemsUrl.expand({
                siteId,
                ...options
            }),
            header
        );

        let systems = get(response, 'data.data', []);

        return systems;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchReportEquipmentTypes = (SiteId, options = {}) => async (getState, dispatch) => {
    const header = await awsHeader();
    options.AllRecords = true;

    try {
        const response = await k2aBaseApi.get(
            EquipmentTypesUrl.expand({
                SiteId,
                ...options
            }),
            header
        );

        let payload = get(response, 'data.data', []);

        return payload;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchReportData = (companyId, resourceId, resourceType) => async (getState, dispatch) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            DownloadUrl.expand({
                companyId,
                resourceId,
                resourceType
            }),
            header
        );

        const fileUrl = response.data.resourceUrl;

        let options = {
            headers: {
                'Content-Type':                 'application/json',
                'Access-Control-Allow-Origin':  '*',
                'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH, OPTIONS',
                'Access-Control-Allow-Headers': 'X-Requested-With, content-type, Authorization'
            }
        };

        const res = await axios.get(fileUrl, options)
            .then((response) => {
                return response.data;
            });

        return res;
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchOptimizationReportData = (payload) => async (getState, dispatch) => {
    const header = await awsHeader();

    if (payload.status === 'Complete') {
        const companyId = payload.companyId;
        const resourceId = payload.result.exportFileKey;
        const resourceType = 'EntityOptimizationExportCreate';
        const fileType = 'csv';
    
        try {
            const response = await k2aBaseApi.get(
                DownloadUrl.expand({
                    companyId,
                    resourceId,
                    resourceType
                }),
                header
            );
        
            const fileUrl = response.data.resourceUrl;
            const fileName = resourceId + '.' + fileType;
            const element = document.createElement('a');
            element.href = fileUrl;
            element.download = `export.${fileType}`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            dispatch(enqueueSnackbar(`${fileName} file downloaded`, 'success'));
        } catch (error) {
            dispatch(handleNetworkError(error));
        }
    }
};

export const fetchEntityRules = (EntityIds, fromDateTime, toDateTime, siteId) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchEntityRules'));

    try {
        const parameters = { 'EntityIds': EntityIds, 'Page': 1, 'Limit': 500 };
        const queryparameterId = await dispatch(storeQueryParamter(parameters));

        const response = await k2aBaseApi.get(
            EntityRuleUrl.expand({
                queryparameterId,
                fromDateTime,
                toDateTime,
                siteId
            }),
            header
        );

        const data = get(response, 'data.data', []);
        const meta = get(response, 'data.meta', {});

        const payloadData =data.map(rule => (
            {...rule, hasFaults: rule.faultCount > 0}
        ));

        dispatch({
            type:    FETCH_ENTITY_RULES,
            payload: {
                data: payloadData,
                meta
            }
        });

        dispatch(setBobLoading('fetchEntityRules'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('fetchEntityRules'));
    }
};

export const fetchReportSensors = (systemIds, equipmentIds) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('fetchEntitySensors'));

    if (isEmpty(getState().sensorTypes.data)) {
        await dispatch(fetchSensorTypes());
    }

    const sensorTypes = getState().sensorTypes.data[0].items;

    const spaceAirTempSensorTypes = sensorTypes.filter(item => item.name === 'Local Space Temperature' || item.name === 'Space Air Temperature').map(item => item.id);
    const outsideAirTempSensorTypes = sensorTypes.filter(item => item.name === 'Outside Air Temperature').map(item => item.id);
    const occupancyStatus = sensorTypes.filter(item => item.name === 'Occupancy Status').map(item => item.id);

    try {
        const spaceAirParameters = { 'systemids': systemIds, 'equipmentids': equipmentIds, 'sensortypeids': spaceAirTempSensorTypes };
        const outsideAirParameters = { 'systemids': systemIds, 'equipmentids': equipmentIds, 'sensortypeids': outsideAirTempSensorTypes };
        const occupancyStatusParameters = { 'systemids': systemIds, 'equipmentids': equipmentIds, 'sensortypeids': occupancyStatus };

        const spaceAirParametersId = await dispatch(storeQueryParamter(spaceAirParameters));
        const outsideAirParametersId = await dispatch(storeQueryParamter(outsideAirParameters));
        const occupancyStatusParametersId = await dispatch(storeQueryParamter(occupancyStatusParameters));

        const spaceAirResponse = await k2aBaseApi.get(
            SensorsUrl.expand({ queryparameterId: spaceAirParametersId }),
            header
        );

        const outsideAirResponse = await k2aBaseApi.get(
            SensorsUrl.expand({ queryparameterId: outsideAirParametersId }),
            header
        );
        
        const occupancyStatusResponse = await k2aBaseApi.get(
            SensorsUrl.expand({ queryparameterId: occupancyStatusParametersId }),
            header
        );
        
        const spaceAirSensors = get(spaceAirResponse, 'data', []);
        const outsideAirSensors = get(outsideAirResponse, 'data', []);
        const occupancyStatusSensors = get(occupancyStatusResponse, 'data', []);

        dispatch({
            type:    FETCH_ENTITY_SENSORS,
            payload: {
                spaceAirSensors,
                outsideAirSensors,
                occupancyStatusSensors
            }
        });

        dispatch(setBobLoading('fetchEntitySensors'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('fetchEntitySensors'));
    }
};

export const fetchReportSensorsbyEquipment = (equipmentIds,sensortypeids) => async (dispatch, getState) => {
    const header = await awsHeader();
    
    dispatch(setBobLoading('fetchEntitySensors'));

   
    try {
        const spaceAirParameters = { 'equipmentids': equipmentIds,'sensortypeids': sensortypeids};
        const spaceAirParametersId = await dispatch(storeQueryParamter(spaceAirParameters));
        const spaceAirResponse = await k2aBaseApi.get(
            SensorsUrl.expand({ queryparameterId: spaceAirParametersId }),
            header
        );
        const spaceAirSensors = get(spaceAirResponse, 'data', []);

        dispatch({
            type:    FETCH_ENTITY_SENSORS,
            payload: {
                spaceAirSensors
            }
        });

        dispatch(setBobLoading('fetchEntitySensors'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('fetchEntitySensors'));
    }
};

export const setEntityRules = (data, meta) => async (dispatch, getState) => {
    dispatch({
        type:    FETCH_ENTITY_RULES,
        payload: {
            data,
            meta
        }
    });
};

export const fetchSensorCountReport = (companyId, clientIds) => async (dispatch, getState) => {
    const header = await awsHeader();
    const body = {};
    dispatch(setBobLoading('fetchSensorScoreReport'));

    try {

        body.clientIds = clientIds;

        const response = await k2aBaseApi.post(
            SensorCountReportUrl.expand({companyId}),
            body,
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: FETCH_SENSOR_COUNT_REPORT,
            payload
        });

        dispatch(setBobLoading('fetchSensorScoreReport'));
    } catch (error) {
        dispatch(handleNetworkError(error));
        dispatch(setBobLoading('fetchSensorScoreReport'));
    }
};

export const setSensorCountReport = (payload) => async (dispatch, getState) => {
    dispatch({
        type: FETCH_SENSOR_COUNT_REPORT,
        payload
    });

};

export const fetchEntitySensors = (siteId, parentId,  options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    if (options.mutateCompanyId) {
        const mutatedCompanyId = getState().bob.targetNavCompany.id;

        header.headers['K2A-Company'] = mutatedCompanyId;
    }
    
    options.limit || (options.limit = 500);
    options.page || (options.page = 1);
    options.searchFilter || (options.searchFilter = '');
    options.sort || (options.sort = DEFAULT_SORT);

    dispatch(setBobLoading('FetchEntitySensors'));

    try {
        const response = await k2aBaseApi.get(
            EntitySensorsUrl.expand({
                siteId,
                parentId,
                ...options
            }),
            header
        );

        let sensors = get(response, 'data.data', []) || [];

        return sensors;

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('FetchEntitySensors'));
    }
};

export const createBillingMetricsReport = (CompanyId, ClientId) => async (dispatch, getState) => {
    const header = await awsHeader();
    const body = {};

    dispatch(setBobLoading('fetchBillingMetricReport'));

    try {
        const response = await k2aBaseApi.post(
            BillingMetricsReportUrl.expand({
                CompanyId,
                ClientId
            }),
            body,
            header
        );
        
        const payload = get(response, 'data.processId', '');

        dispatch(setProcessId(payload));
        return true;
    }
    catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
    finally {
        dispatch(setBobLoading('fetchBillingMetricReport'));
    }
};

export const fetchBillingMetricReport = payload => async (dispatch, getState) => {
    const header = await awsHeader();

    if (payload.status === 'Complete') {
        const companyId = payload.companyId;
        const resourceId = payload.result.exportFileKey;
        const resourceType = 'EntityOptimizationExportCreate';
        const fileType = 'csv';
    
        try {
            const response = await k2aBaseApi.get(
                DownloadUrl.expand({
                    companyId,
                    resourceId,
                    resourceType
                }),
                header
            );
        
            const fileUrl = response.data.resourceUrl;
            const fileName = resourceId + '.' + fileType;
            const element = document.createElement('a');
            element.href = fileUrl;
            element.download = `export.${fileType}`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            dispatch(enqueueSnackbar(`${fileName} file downloaded`, 'success'));
        } catch (error) {
            dispatch(handleNetworkError(error));
        }
    }    
};

export const displayErrorMessage = (message) => async (dispatch, getState) => {
    dispatch(enqueueSnackbar(message, 'error'));
};
